import React from 'react';
import {
    cn,
    stopPropagation,
    TOOLBAR_BUTTON_CLASS,
    type CarouselState,
    type ZoomPanView
} from './imageCarouselShared';
import { PreviewActionMenu } from './PreviewActionMenu';
import type { PreviewAction } from './previewActionMenuModel';

const ThumbnailsIcon: React.FC = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="w-4 h-4"
    >
        <path d="M2 6.75A.75.75 0 012.75 6h3a.75.75 0 01.75.75v6.5a.75.75 0 01-.75.75h-3a.75.75 0 01-.75-.75v-6.5zM8.25 6.75A.75.75 0 019 6h2a.75.75 0 01.75.75v6.5a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v-6.5zM14.25 6a.75.75 0 00-.75.75v6.5c0 .414.336.75.75.75h3a.75.75 0 00.75-.75v-6.5a.75.75 0 00-.75-.75h-3z" />
    </svg>
);

const CollapseIcon: React.FC = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="w-4 h-4"
    >
        <path d="M3.28 2.22a.75.75 0 00-1.06 1.06L5.44 6.5H2.75a.75.75 0 000 1.5h4.5A.75.75 0 008 7.25v-4.5a.75.75 0 00-1.5 0v2.69L3.28 2.22zM13.5 2.75a.75.75 0 00-1.5 0v4.5c0 .414.336.75.75.75h4.5a.75.75 0 000-1.5h-2.69l3.22-3.22a.75.75 0 00-1.06-1.06L13.5 5.44V2.75zM3.28 17.78l3.22-3.22v2.69a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.69l-3.22 3.22a.75.75 0 101.06 1.06zM13.5 14.56l3.22 3.22a.75.75 0 101.06-1.06l-3.22-3.22h2.69a.75.75 0 000-1.5h-4.5a.75.75 0 00-.75.75v4.5a.75.75 0 001.5 0v-2.69z" />
    </svg>
);

const ExpandIcon: React.FC = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="w-4 h-4"
    >
        <path d="M13.28 7.78l3.22-3.22v2.69a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.69l-3.22 3.22a.75.75 0 001.06 1.06zM2 17.25v-4.5a.75.75 0 011.5 0v2.69l3.22-3.22a.75.75 0 011.06 1.06L4.56 16.5h2.69a.75.75 0 010 1.5h-4.5a.747.747 0 01-.75-.75zM12.22 13.28l3.22 3.22h-2.69a.75.75 0 000 1.5h4.5a.747.747 0 00.75-.75v-4.5a.75.75 0 00-1.5 0v2.69l-3.22-3.22a.75.75 0 10-1.06 1.06zM3.5 4.56l3.22 3.22a.75.75 0 001.06-1.06L4.56 3.5h2.69a.75.75 0 000-1.5h-4.5A.75.75 0 002 2.75v4.5a.75.75 0 001.5 0V4.56z" />
    </svg>
);

const ChevronIcon: React.FC<{ direction: 'left' | 'right' }> = ({ direction }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="w-6 h-6 text-gray-700"
    >
        <path
            fillRule="evenodd"
            d={
                direction === 'left'
                    ? 'M12.78 4.22a.75.75 0 010 1.06L8.56 9.5l4.22 4.22a.75.75 0 11-1.06 1.06L7 10.56a.75.75 0 010-1.06l4.72-4.72a.75.75 0 011.06 0z'
                    : 'M7.22 15.78a.75.75 0 010-1.06L11.44 11.5 7.22 7.28a.75.75 0 111.06-1.06L13 10.44a.75.75 0 010 1.06l-4.72 4.72a.75.75 0 01-1.06 0z'
            }
            clipRule="evenodd"
        />
    </svg>
);

const ToolbarDivider: React.FC = () => <div className="w-px h-4 bg-white/30 mx-0.5" />;

interface ToolbarButtonProps {
    onClick: () => void;
    label: string;
    title?: string;
    pressed?: boolean;
    className?: string;
    children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
    onClick,
    label,
    title,
    pressed,
    className,
    children
}) => (
    <button
        type="button"
        onClick={onClick}
        aria-label={label}
        aria-pressed={pressed}
        title={title}
        className={cn(TOOLBAR_BUTTON_CLASS, className ?? 'text-white')}
    >
        {children}
    </button>
);

interface ThumbnailToggle {
    visible: boolean;
    onToggle: () => void;
}

interface CarouselToolbarProps {
    view: ZoomPanView;
    mirrored: boolean;
    isFullscreen: boolean;
    onToggleFullscreen: () => void;
    thumbnailToggle: ThumbnailToggle | null;
    actions: PreviewAction[];
}

const CarouselToolbar: React.FC<CarouselToolbarProps> = ({
    view,
    mirrored,
    isFullscreen,
    onToggleFullscreen,
    thumbnailToggle,
    actions
}) => (
    <div
        className={cn(
            'absolute top-3 z-20 flex items-center gap-1 bg-black/50 backdrop-blur-sm rounded-lg p-1',
            mirrored ? 'left-3' : 'right-3'
        )}
        onDoubleClick={stopPropagation}
        onMouseDown={stopPropagation}
        onMouseUp={stopPropagation}
        onWheel={stopPropagation}
    >
        <ToolbarButton onClick={view.zoomOut} label="Zoom out" className="text-white text-base">
            −
        </ToolbarButton>
        <span className="text-xs text-white px-1 min-w-[2.5rem] text-center">
            {Math.round(view.zoom * 100)}%
        </span>
        <ToolbarButton onClick={view.zoomIn} label="Zoom in" className="text-white text-base">
            +
        </ToolbarButton>
        {thumbnailToggle && (
            <>
                <ToolbarDivider />
                <ToolbarButton
                    onClick={thumbnailToggle.onToggle}
                    pressed={thumbnailToggle.visible}
                    label={thumbnailToggle.visible ? 'Hide thumbnails' : 'Show thumbnails'}
                    title={thumbnailToggle.visible ? 'Hide thumbnails' : 'Show thumbnails'}
                    className={thumbnailToggle.visible ? 'text-white' : 'text-white/50'}
                >
                    <ThumbnailsIcon />
                </ToolbarButton>
            </>
        )}
        <ToolbarDivider />
        <ToolbarButton
            onClick={onToggleFullscreen}
            label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
            {isFullscreen ? <CollapseIcon /> : <ExpandIcon />}
        </ToolbarButton>
        {actions.length > 0 && (
            <>
                <ToolbarDivider />
                <PreviewActionMenu actions={actions} mirrored={mirrored} />
            </>
        )}
    </div>
);

interface ThumbnailRailProps {
    thumbs: string[];
    activeIndex: number;
    labels?: string[];
    onSelect: (index: number) => void;
}

// Sits outside the image, so it uses neutral rings rather than the
// white-on-photo treatment an overlay needs.
const ThumbnailRail: React.FC<ThumbnailRailProps> = ({ thumbs, activeIndex, labels, onSelect }) => {
    const itemRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

    React.useLayoutEffect(() => {
        itemRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, [activeIndex]);

    return (
        <div
            className="shrink-0 flex flex-col gap-2 overflow-y-auto scrollbar-hide py-0.5 px-0.5"
            onDoubleClick={stopPropagation}
            onMouseDown={stopPropagation}
            onMouseUp={stopPropagation}
        >
            {thumbs.map((thumb, i) => (
                <button
                    key={i}
                    ref={(el) => {
                        itemRefs.current[i] = el;
                    }}
                    type="button"
                    aria-label={labels?.[i] ?? `Go to image ${i + 1}`}
                    title={labels?.[i]}
                    aria-current={i === activeIndex}
                    onClick={() => onSelect(i)}
                    onDoubleClick={stopPropagation}
                    onMouseDown={stopPropagation}
                    className={cn(
                        'shrink-0 w-16 h-16 rounded-md overflow-hidden bg-white transition-all',
                        i === activeIndex
                            ? 'border-2 border-gray-900 opacity-100'
                            : 'border border-gray-200 opacity-90 hover:opacity-100 hover:border-gray-400'
                    )}
                >
                    <img src={thumb} alt="" loading="lazy" className="w-full h-full object-cover" />
                </button>
            ))}
        </div>
    );
};

interface DotPagerProps {
    count: number;
    activeIndex: number;
    onSelect: (index: number) => void;
}

const DotPager: React.FC<DotPagerProps> = ({ count, activeIndex, onSelect }) => (
    <div
        className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10 max-w-[calc(100%-1.5rem)]"
        onDoubleClick={stopPropagation}
        onMouseDown={stopPropagation}
        onMouseUp={stopPropagation}
    >
        {Array.from({ length: count }, (_, i) => (
            <button
                key={i}
                type="button"
                aria-label={`Go to image ${i + 1}`}
                aria-current={i === activeIndex}
                onClick={() => onSelect(i)}
                onDoubleClick={stopPropagation}
                onMouseDown={stopPropagation}
                className={cn(
                    'w-2 h-2 rounded-full ring-1 ring-gray-300 transition-colors',
                    i === activeIndex ? 'bg-gray-800' : 'bg-white/80 hover:bg-white'
                )}
            />
        ))}
    </div>
);

const ARROW_BUTTON_CLASS =
    'absolute top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors z-10';

const ArrowPager: React.FC<{ onPrev: () => void; onNext: () => void }> = ({ onPrev, onNext }) => (
    <>
        <button
            type="button"
            onClick={onPrev}
            onDoubleClick={stopPropagation}
            onMouseDown={stopPropagation}
            className={cn(ARROW_BUTTON_CLASS, 'left-4')}
            aria-label="Previous image"
        >
            <ChevronIcon direction="left" />
        </button>
        <button
            type="button"
            onClick={onNext}
            onDoubleClick={stopPropagation}
            onMouseDown={stopPropagation}
            className={cn(ARROW_BUTTON_CLASS, 'right-4')}
            aria-label="Next image"
        >
            <ChevronIcon direction="right" />
        </button>
    </>
);

const LabelChip: React.FC<{ label: string; mirrored: boolean }> = ({ label, mirrored }) => (
    <div
        className={cn(
            'absolute top-3 z-20 px-2 py-1 rounded-md bg-gray-800/70 text-white text-xs font-medium',
            mirrored ? 'right-3' : 'left-3'
        )}
    >
        {label}
    </div>
);

interface ImageLayerProps {
    images: string[];
    activeIndex: number;
    view: ZoomPanView;
}

const ImageLayer: React.FC<ImageLayerProps> = ({ images, activeIndex, view }) => (
    <>
        {images.map((url, i) => {
            const active = i === activeIndex;
            return (
                <img
                    key={url}
                    src={url}
                    alt={`Preview ${i + 1}`}
                    className={cn(
                        'absolute inset-0 w-full h-full object-contain transition-opacity duration-200',
                        active ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    )}
                    style={
                        active
                            ? {
                                  transform: `translate(${view.offset.x}px, ${view.offset.y}px) scale(${view.zoom})`,
                                  transformOrigin: 'center center',
                                  cursor:
                                      view.zoom > 1
                                          ? view.isPanning
                                              ? 'grabbing'
                                              : 'grab'
                                          : 'default'
                              }
                            : undefined
                    }
                    loading="eager"
                />
            );
        })}
    </>
);

interface CarouselOverlaysProps {
    view: ZoomPanView;
    state: CarouselState;
    mirrored: boolean;
    isLoading: boolean;
    showPager: boolean;
    hasThumbStrip: boolean;
    imageCount: number;
    actions: PreviewAction[];
}

const CarouselOverlays: React.FC<CarouselOverlaysProps> = ({
    view,
    state,
    mirrored,
    isLoading,
    showPager,
    hasThumbStrip,
    imageCount,
    actions
}) => (
    <>
        {!isLoading && (
            <CarouselToolbar
                view={view}
                mirrored={mirrored}
                isFullscreen={state.isFullscreen}
                onToggleFullscreen={state.toggleFullscreen}
                thumbnailToggle={
                    hasThumbStrip
                        ? { visible: state.showThumbnails, onToggle: state.toggleThumbnails }
                        : null
                }
                actions={actions}
            />
        )}
        {showPager && state.hasMultiple && (
            <ArrowPager onPrev={state.goPrev} onNext={state.goNext} />
        )}
        {showPager && (
            <DotPager count={imageCount} activeIndex={state.safeIndex} onSelect={state.setIndex} />
        )}
    </>
);

interface CarouselStageProps {
    containerRef: React.RefObject<HTMLDivElement | null>;
    images: string[];
    view: ZoomPanView;
    state: CarouselState;
    labels?: string[];
    mirrored: boolean;
    isLoading: boolean;
    showPager: boolean;
    hasThumbStrip: boolean;
    actions: PreviewAction[];
}

export const CarouselStage: React.FC<CarouselStageProps> = ({
    containerRef,
    images,
    view,
    state,
    labels,
    mirrored,
    isLoading,
    showPager,
    hasThumbStrip,
    actions
}) => {
    const label = labels?.[state.safeIndex];

    return (
        <div
            ref={containerRef}
            tabIndex={0}
            className={cn(
                'bg-gray-200 overflow-hidden select-none focus:outline-none relative flex-1 min-w-0 h-full',
                !state.isFullscreen && 'rounded-lg'
            )}
            {...view.handlers}
        >
            {images.length === 0 ? (
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                    No images
                </div>
            ) : (
                <>
                    <ImageLayer images={images} activeIndex={state.safeIndex} view={view} />
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white">
                            <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-700 animate-spin" />
                        </div>
                    )}
                    {!isLoading && label && <LabelChip label={label} mirrored={mirrored} />}
                    <CarouselOverlays
                        view={view}
                        state={state}
                        mirrored={mirrored}
                        isLoading={isLoading}
                        showPager={showPager}
                        hasThumbStrip={hasThumbStrip}
                        imageCount={images.length}
                        actions={actions}
                    />
                </>
            )}
        </div>
    );
};

interface CarouselFrameProps {
    className?: string;
    mirrored: boolean;
    railThumbs: string[] | null;
    labels?: string[];
    state: CarouselState;
    children: React.ReactNode;
}

export const CarouselFrame: React.FC<CarouselFrameProps> = ({
    className,
    mirrored,
    railThumbs,
    labels,
    state,
    children
}) => {
    const rail = railThumbs ? (
        <ThumbnailRail
            thumbs={railThumbs}
            activeIndex={state.safeIndex}
            labels={labels}
            onSelect={state.setIndex}
        />
    ) : null;

    return (
        <>
            {state.isFullscreen && <div className="fixed inset-0 z-50 bg-black/90" />}
            <div
                className={cn(
                    'flex gap-1.5 min-h-0',
                    state.isFullscreen ? 'fixed inset-0 z-[51] bg-black/95 p-3' : 'w-full h-full',
                    !state.isFullscreen && className
                )}
            >
                {mirrored && rail}
                {children}
                {!mirrored && rail}
            </div>
        </>
    );
};
